// ─────────────────────────────────────────
// CallScreen — extracted from App.js
// Pure presentational component for the active call UI
// ─────────────────────────────────────────

function fmtTime(s) {
  return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
}

const SOUND_MAP = {
  laughter:  { icon:"😄", color:"#f59e0b", bg:"rgba(245,158,11,0.12)", border:"rgba(245,158,11,0.4)", label:"LAUGHTER",   pulse:"rgba(245,158,11,0.3)" },
  applause:  { icon:"👏", color:"#10b981", bg:"rgba(16,185,129,0.12)",  border:"rgba(16,185,129,0.4)",  label:"APPLAUSE",   pulse:"rgba(16,185,129,0.3)" },
  clapping:  { icon:"👏", color:"#10b981", bg:"rgba(16,185,129,0.12)",  border:"rgba(16,185,129,0.4)",  label:"CLAPPING",   pulse:"rgba(16,185,129,0.3)" },
  music:     { icon:"🎵", color:"#8b5cf6", bg:"rgba(139,92,246,0.12)",  border:"rgba(139,92,246,0.4)",  label:"MUSIC",      pulse:"rgba(139,92,246,0.3)" },
  crying:    { icon:"😢", color:"#93b4ff", bg:"rgba(147,180,255,0.12)", border:"rgba(147,180,255,0.4)", label:"CRYING",     pulse:"rgba(147,180,255,0.3)" },
  coughing:  { icon:"🤧", color:"#94a3b8", bg:"rgba(148,163,184,0.12)", border:"rgba(148,163,184,0.4)", label:"COUGHING",   pulse:"rgba(148,163,184,0.3)" },
  sighing:   { icon:"😮‍💨", color:"#94a3b8", bg:"rgba(148,163,184,0.12)", border:"rgba(148,163,184,0.4)", label:"SIGHING",    pulse:"rgba(148,163,184,0.3)" },
  tapping:   { icon:"👆", color:"#f97316", bg:"rgba(249,115,22,0.12)",  border:"rgba(249,115,22,0.4)",  label:"TAPPING",    pulse:"rgba(249,115,22,0.3)" },
  gibberish: { icon:"🌀", color:"#ec4899", bg:"rgba(236,72,153,0.12)",  border:"rgba(236,72,153,0.4)",  label:"GIBBERISH",  pulse:"rgba(236,72,153,0.3)" },
  noise:     { icon:"📢", color:"#f43f5e", bg:"rgba(244,63,94,0.12)",   border:"rgba(244,63,94,0.4)",   label:"NOISE",      pulse:"rgba(244,63,94,0.3)" },
};

const DEFAULT_SOUND = { icon:"🔊", color:"#f59e0b", bg:"rgba(245,158,11,0.12)", border:"rgba(245,158,11,0.4)", label:"SOUND", pulse:"rgba(245,158,11,0.3)" };

export default function CallScreen({
  // State
  callState, callMode, agentMode, agentThinking,
  transcript, input, error, duration, isListening,
  flashEffect, flashKey, shortcutSuggestion, avatarId, css,
  translatorEnabled, targetLanguage,
  // Functions
  sendText, endCall, handleInputChange, applyShortcut,
  takeOver, handBack, startListening, stopListening, setError,
  // Refs
  endRef, taRef,
  // Components
  Avatar, IconPhone, IconClose, IconMic, IconSend,
}) {
  const avatarState = callState==="active"
    ? (transcript.length>0 && transcript[transcript.length-1].speaker==="other" ? "listening" : "speaking")
    : "idle";

  return (
    <>
      <style>{css}</style>
      {/* Flash effects */}
      {flashEffect === "connect" && (
        <div key={"c"+flashKey} style={{position:"fixed",inset:0,zIndex:99999,pointerEvents:"none",
          background:"rgba(16,185,129,0.15)",
          boxShadow:"inset 0 0 0 7px rgba(16,185,129,0.95)",
          animation:"flashConnect 0.9s ease-out forwards"}}/>
      )}
      {flashEffect === "disconnect" && (
        <div key={"d"+flashKey} style={{position:"fixed",inset:0,zIndex:99999,pointerEvents:"none",
          background:"rgba(244,63,94,0.15)",
          boxShadow:"inset 0 0 0 7px rgba(244,63,94,0.95)",
          animation:"flashDisconnectBorder 0.9s ease-out forwards"}}/>
      )}

      <div className="screen-enter" style={{height:"100dvh",display:"flex",flexDirection:"column",background:"var(--bg)",paddingTop:"env(safe-area-inset-top)",paddingBottom:"env(safe-area-inset-bottom)"}}>

        {/* Header */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px 10px",borderBottom:"1px solid var(--s3)",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:callState==="ringing"||callState==="active"?"var(--ok)":callState==="error"?"var(--err)":"var(--b2)",animation:callState==="active"?"dotPulse 2s infinite":"none"}}/>
            <span style={{fontSize:12,fontFamily:"var(--mono)",color:callState==="active"?"var(--ok)":"var(--tx2)",letterSpacing:"0.06em",fontWeight:600}}>{callState.toUpperCase()}</span>
            <span style={{fontSize:12,fontFamily:"var(--mono)",color:"var(--tx3)"}}>·</span>
            <span style={{fontSize:12,fontFamily:"var(--mono)",color:"var(--tx3)"}}>{fmtTime(duration)}</span>
            {translatorEnabled && targetLanguage && (
              <span
                title={`Translator on — ${targetLanguage.name}`}
                style={{
                  display:"inline-flex",alignItems:"center",gap:4,
                  padding:"3px 8px",borderRadius:999,
                  background:"rgba(139,92,246,0.15)",
                  border:"1px solid rgba(139,92,246,0.4)",
                  fontSize:10,fontFamily:"var(--mono)",fontWeight:700,
                  color:"#a78bfa",letterSpacing:"0.04em",
                }}
              >
                <span style={{fontSize:12,lineHeight:1}}>🌐</span>
                {targetLanguage.flag} {targetLanguage.code.toUpperCase()}
              </span>
            )}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            {callMode==="agent" && agentMode==="agent" && (
              <button onClick={takeOver} style={{background:"#065f46",color:"#6ee7b7",borderRadius:20,padding:"5px 14px",fontSize:11,fontWeight:700,border:"1px solid #10b981",letterSpacing:"0.02em"}}>Take Over</button>
            )}
            {callMode==="agent" && agentMode==="manual" && (
              <button onClick={handBack} style={{background:"var(--ac3)",color:"var(--ac2)",borderRadius:20,padding:"5px 14px",fontSize:11,fontWeight:700,border:"1px solid var(--ac)"}}>Hand Back</button>
            )}
            <button onClick={endCall} style={{background:"var(--err)",color:"#fff",borderRadius:20,padding:"5px 14px",fontSize:11,fontWeight:700,display:"flex",alignItems:"center",gap:4}}>
              <IconPhone size={11} color="#fff"/>End
            </button>
          </div>
        </div>

        {/* Error bar */}
        {error && (
          <div style={{background:"rgba(244,63,94,0.06)",borderBottom:"1px solid var(--err)",padding:"8px 16px",fontSize:12,color:"var(--err)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>{error}</span>
            <button onClick={()=>setError(null)} style={{color:"var(--err)",display:"flex",alignItems:"center",padding:4}}><IconClose size={14} color="var(--err)"/></button>
          </div>
        )}

        {/* Transcript area */}
        <div style={{flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:8}}>
          {transcript.length===0 && (
            <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
              <div className={callState!=="active"?"connecting-avatar":""}>
                <Avatar avatarId={avatarId} state={avatarState} size={80}/>
              </div>
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                <span style={{fontSize:13,fontFamily:"var(--mono)",color:callState==="active"?"var(--ok)":"var(--tx3)",fontWeight:600,letterSpacing:"0.06em"}}>
                  {callState==="connecting"?"CONNECTING…":callState==="ringing"?"RINGING…":callState==="active"?"LISTENING…":"WAITING…"}
                </span>
                {callState==="active" && (
                  <>
                    <div style={{display:"flex",gap:3,alignItems:"center",marginTop:4}}>
                      {[0,1,2,3,4].map(i=>(
                        <div key={i} style={{width:3,height:12,borderRadius:2,background:"var(--ok)",opacity:0.7,animation:`pulse 1s ease-in-out ${i*0.12}s infinite alternate`}}/>
                      ))}
                    </div>
                    <span style={{fontSize:12,color:"var(--tx3)",textAlign:"center",maxWidth:220,lineHeight:1.5,marginTop:4}}>Transcription appears here when the other person speaks</span>
                  </>
                )}
              </div>
            </div>
          )}

          {(() => {
            // Diarization: if Grok detected 2+ distinct speakers on this call,
            // show a small speaker label above each incoming bubble. On single-
            // speaker calls (the normal case) nothing changes visually.
            const distinctSpeakers = new Set();
            for (const l of transcript) if (l.speakerId) distinctSpeakers.add(l.speakerId);
            const showSpeakerLabels = distinctSpeakers.size >= 2;
            return transcript.map((l,i)=>(
            <div key={i} className={l.speaker==="user"?"bubble-user":(l.speaker==="intro"||l.speaker==="sound")?"bubble-center":"bubble-other"} style={{display:"flex",justifyContent:l.speaker==="user"?"flex-end":(l.speaker==="intro"||l.speaker==="sound")?"center":"flex-start"}}>
              {l.speaker==="intro" ? (
                <div style={{padding:"6px 16px",borderRadius:20,background:"var(--s2)",border:"1px solid var(--s3)",fontSize:12,color:"var(--tx3)",fontFamily:"var(--mono)"}}>
                  {l.text}
                </div>
              ) : l.speaker==="sound" ? (
                (() => {
                  const s = SOUND_MAP[l.text?.toLowerCase()] || { ...DEFAULT_SOUND, label: (l.text||"SOUND").toUpperCase() };
                  return (
                    <div style={{
                      padding:"6px 14px 6px 10px", borderRadius:24,
                      background:s.bg, border:`1.5px solid ${s.border}`,
                      fontSize:12, color:s.color, fontFamily:"var(--mono)",
                      display:"flex", alignItems:"center", gap:8,
                      animation:"soundPop 0.35s cubic-bezier(0.34,1.56,0.64,1) both",
                      "--sc": s.pulse,
                      boxShadow:`0 0 0 0 ${s.pulse}`,
                    }}>
                      <span style={{fontSize:20,lineHeight:1,animation:"soundRipple 1s ease-out",display:"inline-block"}}>{s.icon}</span>
                      <div style={{display:"flex",flexDirection:"column",gap:1}}>
                        <span style={{letterSpacing:"0.1em",fontWeight:600,fontSize:11}}>{s.label}</span>
                        <span style={{fontSize:9,opacity:0.5,letterSpacing:"0.08em"}}>NON-VERBAL SOUND</span>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:3,maxWidth:"80%",alignItems:l.speaker==="user"?"flex-end":"flex-start"}}>
                  {l.isIntro && (
                    <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",textAlign:"right",letterSpacing:"0.06em"}}>INTRO MESSAGE</div>
                  )}
                  {showSpeakerLabels && l.speakerId && l.speaker==="other" && (
                    <div style={{fontSize:9,fontFamily:"var(--mono)",color:"var(--tx3)",letterSpacing:"0.08em",paddingLeft:6}}>
                      {l.speakerId.replace("speaker_", "SPEAKER ")}
                    </div>
                  )}
                  <div style={{padding:"10px 14px",borderRadius:l.speaker==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:l.isIntro?"var(--s2)":l.speaker==="user"?"var(--ac3)":"var(--s1)",border:`1px solid ${l.isIntro?"var(--s3)":l.speaker==="user"?"var(--ac)":"var(--s3)"}`,fontSize:15,lineHeight:1.55,color:l.isIntro?"var(--tx3)":l.speaker==="user"?"var(--ac2)":"var(--tx)",fontStyle:l.isIntro?"italic":"normal"}}>
                    {l.text}
                    {l.translating && <span style={{fontSize:11,color:"#8b5cf6",fontFamily:"var(--mono)",marginLeft:6}}>translating...</span>}
                  </div>
                  {l.originalText && (
                    <div style={{fontSize:11,color:"var(--tx3)",fontFamily:"var(--mono)",paddingLeft:6,opacity:0.7,fontStyle:"italic"}}>
                      🌐 {l.originalText}
                    </div>
                  )}
                  {l.translatedText && l.speaker==="user" && (
                    <div style={{fontSize:11,color:"#a78bfa",fontFamily:"var(--mono)",paddingRight:6,textAlign:"right",opacity:0.8}}>
                      🌐 Spoken: {l.translatedText}
                    </div>
                  )}
                  {l.mood && l.speaker==="other" && (
                    <div style={{display:"flex",alignItems:"center",gap:4,paddingLeft:6}}>
                      <div className="mood-dot" style={{width:5,height:5,borderRadius:"50%",background:l.mood.color}}/>
                      <span style={{fontSize:9,fontFamily:"var(--mono)",color:l.mood.color,fontWeight:700,letterSpacing:"0.05em"}}>{l.mood.label.toUpperCase()}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ));
          })()}
          <div ref={endRef}/>
        </div>

        {/* Agent status bar */}
        {callMode==="agent" && agentMode==="agent" && (
          <div style={{padding:"8px 16px",background:"#052e16",borderTop:"1px solid #10b981",display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:agentThinking?"#10b981":"#065f46",boxShadow:agentThinking?"0 0 8px #10b981":"none",transition:"all .3s"}}/>
            <span style={{fontSize:11,fontFamily:"var(--mono)",color:"#6ee7b7",fontWeight:500}}>{agentThinking?"Claude is thinking…":"AI Agent active"}</span>
          </div>
        )}

        {/* Shortcut suggestion */}
        {shortcutSuggestion && (
          <div style={{padding:"6px 16px 0",flexShrink:0}}>
            <button onClick={applyShortcut} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",borderRadius:10,background:"var(--ac3)",border:"1px solid var(--ac)",width:"100%",textAlign:"left"}}>
              <span style={{fontFamily:"var(--mono)",fontSize:11,fontWeight:700,color:"var(--ac2)",flexShrink:0}}>{shortcutSuggestion.key}</span>
              <span style={{fontSize:11,color:"var(--tx)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",flex:1}}>{shortcutSuggestion.value}</span>
              <span style={{fontSize:10,color:"var(--ac2)",fontFamily:"var(--mono)",flexShrink:0}}>TAP</span>
            </button>
          </div>
        )}

        {/* Input area */}
        <div style={{padding:"10px 16px 16px",borderTop:"1px solid var(--s3)",flexShrink:0}}>
          <div style={{display:"flex",gap:8,alignItems:"flex-end",background:"var(--s1)",border:"1px solid var(--s3)",borderRadius:16,padding:"10px 12px"}}>
            <button onClick={isListening?stopListening:startListening} style={{width:34,height:34,borderRadius:"50%",background:isListening?"var(--err)":"var(--s2)",border:`1px solid ${isListening?"var(--err)":"var(--s3)"}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
              <IconMic size={15} color={isListening?"#fff":"var(--tx3)"}/>
            </button>
            <textarea ref={taRef} value={input} onChange={e=>handleInputChange(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendText();}}}
              onInput={e=>{e.target.style.height="auto";e.target.style.height=e.target.scrollHeight+"px";}}
              placeholder="Type to speak…" rows={1}
              style={{flex:1,resize:"none",fontSize:15,lineHeight:1.5,color:"var(--tx)",caretColor:"var(--ac)",maxHeight:80,overflowY:"auto",background:"none",padding:"4px 0"}}/>
            <button onClick={sendText} disabled={!input.trim()} style={{width:34,height:34,borderRadius:"50%",background:input.trim()?"var(--ac)":"var(--s3)",color:input.trim()?"#fff":"var(--tx3)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s"}}>
              <IconSend size={14} color={input.trim()?"#fff":"var(--tx3)"}/>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
